// Passation des responsabilités (lot L9) : choix faits à l'écran avant la sortie d'une personne (ou pour réaffecter son équipe).
// Fonctions pures, testées dans handover.test.js. Le serveur reste la seule autorité : il revalide le graphe final complet.
//
// Un choix de subordonné : { kind: "collaborator" | "account", id, label } | { kind: "vacant" } | absent (pas encore choisi).
// Un choix de poste de service : { kind: "person", id, label } | { kind: "promote" } (le remplaçant devient chef) | { kind: "vacant" } | absent.

export const SLOT_LABELS = { head: "Chef du service", deputy: "Remplaçant" };

export function emptyChoices() {
  return { reports: {}, services: {} };
}

// Les postes que la personne tient dans un service : un seul choix à faire par poste.
export function heldSlots(service) {
  return service.slots || [];
}

export function canPromote(service) {
  return heldSlots(service).includes("head") && !!service.deputy?.id && !heldSlots(service).includes("deputy");
}

export function setReportChoice(choices, reportId, choice) {
  const reports = { ...choices.reports };
  if (choice) reports[reportId] = choice;
  else delete reports[reportId];
  return { ...choices, reports };
}

export function setSlotChoice(choices, serviceId, slot, choice) {
  const current = { ...(choices.services[serviceId] || {}) };
  if (choice) current[slot] = choice;
  else delete current[slot];
  return { ...choices, services: { ...choices.services, [serviceId]: current } };
}

// « Tout confier à X » / « Tout remonter chez le responsable » : remplit les subordonnés encore sans choix, ou tous si `overwrite`.
export function assignAllReports(impact, choices, choice, overwrite = false) {
  const reports = { ...choices.reports };
  for (const report of impact.reports) {
    if (overwrite || !reports[report.id]) reports[report.id] = choice;
  }
  return { ...choices, reports };
}

// Les choix qui ne concernent plus rien de l'impact actuel (quelqu'un a déjà été réaffecté ailleurs) sont écartés.
export function pruneChoices(impact, choices) {
  const reportIds = new Set(impact.reports.map((r) => r.id));
  const reports = Object.fromEntries(Object.entries(choices.reports).filter(([id]) => reportIds.has(id)));
  const services = {};
  for (const service of impact.services) {
    if (choices.services[service.id]) services[service.id] = choices.services[service.id];
  }
  return { reports, services };
}

export function isSlotDecided(service, slot, choices) {
  return !!(choices.services[service.id] || {})[slot];
}

export function pendingCount(impact, choices) {
  const reports = impact.reports.filter((r) => !choices.reports[r.id]).length;
  const slots = impact.services.reduce((n, s) => n + heldSlots(s).filter((slot) => !isSlotDecided(s, slot, choices)).length, 0);
  return reports + slots;
}

export function totalChoices(impact) {
  return impact.reports.length + impact.services.reduce((n, s) => n + heldSlots(s).length, 0);
}

// Bloquant pour la personne qui regarde : des subordonnés hors de son périmètre, ou des services qu'elle ne peut pas gérer.
export function blockedReason(impact) {
  if (!impact?.required) return null;
  if (impact.others_hidden > 0 && impact.blocked_for_caller) {
    return `${impact.others_hidden} subordonné${impact.others_hidden > 1 ? "s" : ""} ${impact.others_hidden > 1 ? "sont" : "est"} hors de votre périmètre : un administrateur doit terminer la passation.`;
  }
  if (impact.services.length > 0 && !impact.services_manageable) {
    return "Changer le chef ou le remplaçant d'un service demande le droit de gérer les services : un administrateur ou un gestionnaire de services doit terminer la passation.";
  }
  return null;
}

function reportMove(report, choice) {
  const move = { collaborator_id: report.id, expected_version: report.version };
  if (choice.kind === "vacant") move.vacant = true;
  else if (choice.kind === "account") move.manager_user_id = choice.id;
  else move.manager_id = choice.id;
  return move;
}

function serviceMove(service, slotChoices) {
  const move = { service_id: service.id, expected_version: service.version };
  const head = slotChoices.head;
  const deputy = slotChoices.deputy;
  if (head?.kind === "promote") {
    move.head_id = service.deputy.id;
    move.deputy_id = null;
  } else if (head) {
    move.head_id = head.kind === "vacant" ? null : head.id;
  }
  if (deputy) move.deputy_id = deputy.kind === "vacant" ? null : deputy.id;
  return move;
}

// `partial` (réaffecter l'équipe) : seuls les éléments décidés partent. Sinon tout doit être décidé, sinon null.
export function buildHandover(impact, choices, { partial = false } = {}) {
  if (!partial && pendingCount(impact, choices) > 0) return null;
  const reports = impact.reports.filter((r) => choices.reports[r.id]).map((r) => reportMove(r, choices.reports[r.id]));
  const services = impact.services
    .filter((s) => heldSlots(s).every((slot) => isSlotDecided(s, slot, choices)) && heldSlots(s).length > 0)
    .map((s) => serviceMove(s, choices.services[s.id]));
  return { reports, services };
}

export function choiceLabel(choice, service) {
  if (!choice) return "À choisir";
  if (choice.kind === "vacant") return "Laissé vacant";
  if (choice.kind === "promote") return `${service?.deputy?.display_name || "Le remplaçant"} devient chef`;
  return choice.label || "Personne choisie";
}

export function summaryLine(impact, choices) {
  const total = totalChoices(impact);
  const done = total - pendingCount(impact, choices);
  return `${done} choix sur ${total}`;
}

export function vacantCount(impact, choices) {
  const reports = impact.reports.filter((r) => choices.reports[r.id]?.kind === "vacant").length;
  const slots = impact.services.reduce((n, s) => n + heldSlots(s).filter((slot) => (choices.services[s.id] || {})[slot]?.kind === "vacant").length, 0);
  return reports + slots;
}

// La réponse 409 `handover_required` / `handover_incomplete` porte l'impact à jour ; on le reprend tel quel.
export function impactFromError(error) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.impact) return detail.impact;
  return null;
}

export function isHandoverConflict(error) {
  const code = error?.response?.data?.detail?.code;
  return code === "handover_incomplete" || code === "handover_required" || code === "version_conflict" || error?.response?.status === 409;
}
